(() => {
  const getTemplateCoordinate = (link, template, axis) => {
    const key = `${template}${axis.toUpperCase()}`;
    const value = Number.parseFloat(link.dataset[key]);

    if (Number.isFinite(value)) return value;

    return axis === 'x'
      ? Number.parseFloat(link.style.getPropertyValue('--hotspot-x')) || 50
      : Number.parseFloat(link.style.getPropertyValue('--hotspot-y')) || 50;
  };

  const initializeRoomBuilder = (builder) => {
    if (!builder || builder.dataset.roomBuilderInitialized === 'true') return;

    const featureLinks = Array.from(builder.querySelectorAll('[data-feature-link]'));
    const templateCards = Array.from(builder.querySelectorAll('[data-template-card]'));
    const templateImages = Array.from(builder.querySelectorAll('[data-template-image]'));
    const rugSceneImages = Array.from(builder.querySelectorAll('[data-rug-scene]'));
    const optionChoices = Array.from(builder.querySelectorAll('[data-slot-option]'));
    const overlayImages = Array.from(builder.querySelectorAll('[data-slot-overlay]'));
    const mobileSlotButtons = Array.from(builder.querySelectorAll('[data-mobile-slot]'));
    const scene = builder.querySelector('[data-room-scene]');
    const roomTitle = builder.querySelector('[data-current-room-title]');
    const roomSubtitle = builder.querySelector('[data-room-subtitle]');
    const featureTitle = builder.querySelector('[data-feature-title]');
    const featureDescription = builder.querySelector('[data-feature-description]');
    const featureDetail = builder.querySelector('[data-feature-detail]');
    const featureNumber = builder.querySelector('[data-feature-number]');
    const featureCta = builder.querySelector('[data-feature-cta]');
    const featureCtaLabel = builder.querySelector('[data-feature-cta-label]');
    const emptyOptions = builder.querySelector('[data-empty-options]');

    if (
      !featureLinks.length ||
      !featureTitle ||
      !featureDescription ||
      !featureDetail ||
      !featureNumber ||
      !featureCta ||
      !featureCtaLabel
    ) {
      return;
    }

    let activeTemplate = 'garage';
    let activeSlot = featureLinks[0].dataset.slot || 'led_wall_art';
    const selectedOptions = {};
    const getClosest = (target, selector) => {
      if (!target || typeof target.closest !== 'function') return null;

      const match = target.closest(selector);
      return match && (typeof builder.contains !== 'function' || builder.contains(match)) ? match : null;
    };

    const templateState = () => {
      if (!selectedOptions[activeTemplate]) selectedOptions[activeTemplate] = {};
      return selectedOptions[activeTemplate];
    };

    const optionMatchesTemplate = (option) => {
      return option.dataset.room === 'all' || option.dataset.room === activeTemplate;
    };

    const getOptionsForSlot = (slot) => {
      return optionChoices.filter((option) => option.dataset.slot === slot && optionMatchesTemplate(option));
    };

    const getFeatureLinkForSlot = (slot) => {
      return featureLinks.find((link) => link.dataset.slot === slot) || featureLinks[0];
    };

    const getFeatureIndex = (slot) => {
      const index = featureLinks.findIndex((link) => link.dataset.slot === slot);
      return index >= 0 ? index : 0;
    };

    const getSelectedOption = (slot) => {
      const options = getOptionsForSlot(slot);
      const selectedId = templateState()[slot];
      const selectedOption = options.find((option) => option.dataset.optionId === selectedId);

      return selectedOption || null;
    };

    const getEmptySlotPrompt = (slot) => {
      switch (slot) {
        case 'led_wall_art':
          return 'Choose an LED wall art option.';
        case 'rugs_carpets':
          return 'Choose a rug to preview it in your room.';
        case 'lighting':
          return 'Choose a lighting option.';
        case 'models_displays':
          return 'Choose a model or display option.';
        case 'desk_decor':
          return 'Choose a desk decor option.';
        case 'garage_decor':
          return 'Choose a garage decor option.';
        default:
          return 'Choose an item option.';
      }
    };

    const ensureTemplateSelections = () => {
      featureLinks.forEach((link) => {
        const slot = link.dataset.slot;
        const selectedOption = getSelectedOption(slot);

        if (selectedOption) {
          templateState()[slot] = selectedOption.dataset.optionId;
        } else {
          delete templateState()[slot];
        }
      });
    };

    const updateHotspotEdgeClasses = (link, x, y) => {
      link.classList.toggle('is-near-left', x < 18);
      link.classList.toggle('is-near-right', x > 82);
      link.classList.toggle('is-near-top', y < 18);
    };

    const updateHotspotPositions = (template) => {
      featureLinks.forEach((link) => {
        const x = getTemplateCoordinate(link, template, 'x');
        const y = getTemplateCoordinate(link, template, 'y');

        link.style.setProperty('--hotspot-x', `${x}%`);
        link.style.setProperty('--hotspot-y', `${y}%`);
        updateHotspotEdgeClasses(link, x, y);
      });
    };

    const getActiveRugScene = () => {
      const selectedRug = getSelectedOption('rugs_carpets');
      if (!selectedRug) return null;

      const candidates = rugSceneImages.filter((image) => {
        const roomMatches = image.dataset.room === activeTemplate;

        return roomMatches && image.dataset.optionId === selectedRug.dataset.optionId;
      });

      return candidates[0] || null;
    };

    const updateSceneImage = () => {
      const activeRugScene = getActiveRugScene();
      const activeTemplateImage = templateImages.find((image) => image.dataset.templateImage === activeTemplate);
      const activeImage = activeRugScene || activeTemplateImage;

      templateImages.forEach((image) => {
        const isSelected = !activeRugScene && image === activeTemplateImage;
        image.classList.toggle('is-active', isSelected);
        image.setAttribute('aria-hidden', isSelected ? 'false' : 'true');
      });

      rugSceneImages.forEach((image) => {
        const isSelected = image === activeRugScene;
        image.classList.toggle('is-active', isSelected);
        image.setAttribute('aria-hidden', isSelected ? 'false' : 'true');
      });

      const stableAspectSource = activeTemplateImage || activeImage;

      if (scene && stableAspectSource && stableAspectSource.dataset.aspect) {
        scene.style.setProperty('--room-builder-scene-aspect-ratio', stableAspectSource.dataset.aspect);
      }
    };

    const updateOverlays = () => {
      overlayImages.forEach((overlay) => {
        const selectedId = templateState()[overlay.dataset.slot];
        const roomMatches = overlay.dataset.room === 'all' || overlay.dataset.room === activeTemplate;
        const isActive = roomMatches && overlay.dataset.optionId === selectedId;

        overlay.classList.toggle('is-active', isActive);
      });
    };

    const updateVisibleOptions = (slot) => {
      const visibleOptions = getOptionsForSlot(slot);
      const selectedOption = getSelectedOption(slot);

      optionChoices.forEach((option) => {
        const isVisible = option.dataset.slot === slot && optionMatchesTemplate(option);
        const isActive = selectedOption && option.dataset.optionId === selectedOption.dataset.optionId;

        option.classList.toggle('is-visible', isVisible);
        option.classList.toggle('is-active', Boolean(isVisible && isActive));
        option.setAttribute('aria-pressed', isVisible && isActive ? 'true' : 'false');
      });

      if (emptyOptions) {
        emptyOptions.classList.toggle('is-visible', visibleOptions.length === 0);
      }
    };

    const selectSlot = (slot) => {
      activeSlot = slot;
      builder.dataset.activeSlot = slot;
      const selectedLink = getFeatureLinkForSlot(slot);
      const selectedOption = getSelectedOption(slot);
      const index = getFeatureIndex(slot);

      featureLinks.forEach((link) => {
        const isSelected = link.dataset.slot === slot;
        link.classList.toggle('is-active', isSelected);

        if (isSelected) {
          link.setAttribute('aria-current', 'true');
        } else {
          link.removeAttribute('aria-current');
        }
      });

      mobileSlotButtons.forEach((button) => {
        button.classList.toggle('is-active', button.dataset.slot === slot);
      });

      featureTitle.textContent = selectedLink.dataset.title || '';
      if (selectedOption) {
        featureDescription.textContent = selectedOption.dataset.optionLabel || '';
        featureDetail.textContent = selectedOption.dataset.optionDescription || '';
      } else {
        featureDescription.textContent = getEmptySlotPrompt(slot);
        featureDetail.textContent =
          slot === 'rugs_carpets'
            ? 'The room will stay on the empty base scene until you select a rug option.'
            : 'Pick an item option below to preview it inside the room.';
      }
      featureNumber.textContent = String(index + 1).padStart(2, '0');
      featureCta.href = selectedOption
        ? selectedOption.dataset.optionLink || selectedLink.dataset.link || '/collections/all'
        : selectedLink.dataset.link || '/collections/all';
      featureCtaLabel.textContent = selectedOption
        ? selectedOption.dataset.optionCta || selectedLink.dataset.cta || 'Shop this slot'
        : selectedLink.dataset.cta || 'Shop this slot';

      updateVisibleOptions(slot);
      updateOverlays();
    };

    const selectOption = (option) => {
      if (!option) return;

      const slot = option.dataset.slot;
      templateState()[slot] = option.dataset.optionId;

      if (slot === 'rugs_carpets') {
        updateSceneImage();
      }

      selectSlot(slot);
    };

    const selectTemplate = (selectedCard) => {
      if (!selectedCard) return;

      activeTemplate = selectedCard.dataset.template || 'garage';

      templateCards.forEach((card) => {
        const isSelected = card === selectedCard;
        card.classList.toggle('is-active', isSelected);
        card.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      });

      if (roomTitle) roomTitle.textContent = selectedCard.dataset.roomTitle || '';
      if (roomSubtitle) roomSubtitle.textContent = selectedCard.dataset.roomDescription || '';

      updateHotspotPositions(activeTemplate);
      ensureTemplateSelections();
      updateSceneImage();
      selectSlot(activeSlot || featureLinks[0].dataset.slot);
    };

    featureLinks.forEach((link) => {
      const updateFeature = () => selectSlot(link.dataset.slot);

      link.addEventListener('pointerenter', updateFeature);
      link.addEventListener('focus', updateFeature);
      link.addEventListener('click', updateFeature);
    });

    optionChoices.forEach((option) => {
      option.addEventListener('click', () => selectOption(option));
    });

    mobileSlotButtons.forEach((button) => {
      button.addEventListener('click', () => selectSlot(button.dataset.slot));
    });

    templateCards.forEach((card) => {
      card.addEventListener('click', () => selectTemplate(card));
      card.addEventListener('focus', () => selectTemplate(card));
    });

    builder.addEventListener('click', (event) => {
      const templateCard = getClosest(event.target, '[data-template-card]');
      if (templateCard) {
        selectTemplate(templateCard);
        return;
      }

      const featureLink = getClosest(event.target, '[data-feature-link]');
      if (featureLink) {
        selectSlot(featureLink.dataset.slot);
        return;
      }

      const option = getClosest(event.target, '[data-slot-option]');
      if (option) {
        selectOption(option);
        return;
      }

      const mobileSlotButton = getClosest(event.target, '[data-mobile-slot]');
      if (mobileSlotButton) {
        selectSlot(mobileSlotButton.dataset.slot);
      }
    });

    builder.addEventListener('pointerover', (event) => {
      const featureLink = getClosest(event.target, '[data-feature-link]');
      if (featureLink) {
        selectSlot(featureLink.dataset.slot);
      }
    });

    builder.addEventListener('focusin', (event) => {
      const templateCard = getClosest(event.target, '[data-template-card]');
      if (templateCard) {
        selectTemplate(templateCard);
        return;
      }

      const featureLink = getClosest(event.target, '[data-feature-link]');
      if (featureLink) {
        selectSlot(featureLink.dataset.slot);
      }
    });

    const initialTemplate = templateCards.find((card) => card.classList.contains('is-active')) || templateCards[0];
    if (initialTemplate) {
      selectTemplate(initialTemplate);
    } else {
      updateHotspotPositions(activeTemplate);
      ensureTemplateSelections();
      selectSlot(activeSlot);
    }

    builder.dataset.roomBuilderInitialized = 'true';
  };

  const initializeAllRoomBuilders = (container = document) => {
    container.querySelectorAll('[data-room-builder]').forEach(initializeRoomBuilder);
  };

  initializeAllRoomBuilders();

  document.addEventListener('shopify:section:load', (event) => {
    initializeAllRoomBuilders(event.target);
  });
})();
